import React from 'react';
import Chip from '@material-ui/core/Chip';
import { useTranslation } from 'react-i18next';
import { formatPercent, formatDateLong } from './formatters';
import type { LifecyclePrediction } from './types';
import css from './DigitalTwinView.module.css';

const formatSignalValue = (value: string): string =>
  value.replace(/[-_]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

interface ReadinessSummaryProps {
  prediction: LifecyclePrediction;
}

export const ReadinessSummary: React.FC<ReadinessSummaryProps> = ({ prediction }) => {
  const { t } = useTranslation();

  const slaughterDateFormatted =
    prediction.slaughterReady?.date != null ? formatDateLong(prediction.slaughterReady.date) : '—';
  const daysText =
    prediction.daysToSlaughter != null
      ? prediction.daysToSlaughter === 1
        ? `1 ${t('digitalTwin.units.day')}`
        : `${prediction.daysToSlaughter} ${t('digitalTwin.units.days')}`
      : '—';
  const hasWithdrawal = prediction.antibioticImpact.hasActiveWithdrawal;

  return (
    <div className={css.kpiRow}>
      <article className={`${css.kpiCard} ${css.kpiCardPrimary}`}>
        <h3 className={css.kpiLabel}>{t('digitalTwin.cards.readiness')}</h3>
        <p className={`${css.kpiValue} ${css.kpiValuePrimary}`}>{formatPercent(prediction.readinessScore)}</p>
        {prediction.confidenceLevel && (
          <p className={css.kpiFootnote}>
            {t('digitalTwin.confidence')}: {formatSignalValue(prediction.confidenceLevel)}
          </p>
        )}
      </article>

      <article className={`${css.kpiCard} ${css.kpiCardAccentOperational}`}>
        <h3 className={css.kpiLabel}>{t('digitalTwin.cards.daysToSlaughter')}</h3>
        <p className={css.kpiValue}>{daysText}</p>
        <p className={css.kpiFootnote}>
          {prediction.daysToSlaughter == null ? t('digitalTwin.insufficientForecast') : slaughterDateFormatted}
        </p>
      </article>

      <article
        className={`${css.kpiCard} ${
          hasWithdrawal ? css.kpiCardAccentComplianceHold : css.kpiCardAccentComplianceClear
        }`}
      >
        <h3 className={css.kpiLabel}>{t('digitalTwin.cards.antibioticStatus')}</h3>
        <Chip
          size="small"
          label={hasWithdrawal ? t('digitalTwin.antibiotic.withdrawalActive') : t('digitalTwin.antibiotic.clear')}
          className={`${css.chipCompliance} ${hasWithdrawal ? css.chipHold : css.chipClear}`}
          variant="outlined"
        />
        <p className={css.kpiFootnote}>
          {t('digitalTwin.dataCompleteness')}: {formatSignalValue(prediction.dataCompleteness)}
        </p>
      </article>
    </div>
  );
};
